import React, { useContext, useState } from 'react';
import { useTranslation } from '@lib/useTypedTranslation';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';

import { AmChart, coreTheme } from '../../../../../../components/chart/amChart';
import { NoSelectionOverlay } from '../../../../../../components/card/noSelectionOverlay';
import { DeviceEvent, DeviceEventName, TzDeviceEvent } from '../../../../../../services/core/eventsTypes';
import { EditEvents, EditEventsAction, useEditEventsDispatch } from '../../../../device/compositions/performance/events/edit-events';
import { deviceEventFilterGroupNames, IDeviceEventFilterState } from '../../../../device/compositions/performance/events/deviceEventsTypes';
import { getDeviceEventFilterGroups } from '../../../../device/compositions/performance/events/deviceEvents';
import { addEventOverlaysToLineChart } from '../../../../device/compositions/performance/charts/lib/eventOverlays';
import { EventsShown } from '../../../../device/compositions/performance/events-shown';
import { ResetZoomButton } from '../../../../device/compositions/performance/charts/lib/resetZoomButton';
import { configureDragToZoom, formatDateAxisLabelsInTimeZone } from '../../../../../../components/chart/lib';
import { DeviceInfoContext } from '../../../../device';
import { anomalyChartNames, AnomalyChartName } from '../../../../device/compositions/performance/charts/lib/chartNames';
import { useFeatureTogglesContext } from '../../../../../../context/featureToggles';

import '../../../../device/compositions/performance/charts/charts.css';
import '../../../../device/compositions/performance/events/deviceEvents.css';
import './deviceEventsTimelineChart.css';

// Marker ticks are sized dynamically (see onDataUpdated's own tickLength calculation below) to
// always reach the actual rendered plot height, rather than a fixed pixel guess - this chart's
// container height is CSS-driven (deviceEventsTimelineChart.css) and shorter than the (y-axis-
// having) charge level chart's own 14rem default, so a value tuned for that chart's height would
// either fall short of or overshoot this one's. FALLBACK_TICK_LENGTH is only used before the
// chart has ever been laid out (e.g. its very first hidden mount - see hideChildren in
// eventTimelineSection.tsx), when plotContainer.pixelHeight is still 0.
const FALLBACK_TICK_LENGTH = 40;
// Matches the exact tick-length-to-label-dy delta already used by DeviceChargeLevel's own
// addEventOverlaysToLineChart call (100px tick / -120px label, i.e. the label sits 20px beyond
// the tick's own length) - keeping the same delta here means the icon sits the same visual
// distance beyond the tick regardless of this chart's (dynamically computed) tick length.
const LABEL_DY_BEYOND_TICK = 20;

// A fixed backing series purely so the DateAxis has enough real data points for
// configureDragToZoom's own zoom-limiting logic (which reads chart.data.length) to allow zooming
// down to a useful granularity - independent of how many (possibly very few, e.g. one) events
// actually occurred that day. The series itself is fully transparent; markers are drawn
// separately, as axis ranges, by addEventOverlaysToLineChart.
const EXTENT_STEP_MINUTES = 5;

function buildExtentData(dateRange: { from: number, to: number }) {
  const points: { local: number, value: number }[] = [];
  const stepMs = EXTENT_STEP_MINUTES * 60 * 1000;
  let index = 0;
  for (let local = dateRange.from; local <= dateRange.to; local += stepMs) {
    // Alternates 0/1 rather than a constant value - DeviceChargeLevel (the only other chart ever
    // safely registered with useAlignCharts) never needs strictMinMax, because its real battery
    // data genuinely varies; forcing a fixed range via strictMinMax against a perfectly flat
    // series turned out to hang amCharts (and the whole tab) once useAlignCharts set this axis's
    // width. Genuinely varying data lets normal auto-scaling land on the same 0-1 range instead,
    // without strictMinMax ever being involved.
    points.push({ local, value: index % 2 });
    index += 1;
  }
  return points;
}

// Mirrors DeviceChargeLevel's own resetZoom, but exported (rather than left inline in the
// component) so its "chart not yet created" guard can be exercised directly by a unit test:
// ResetZoomButton's own disabled={!zoomedIn} means this is only ever reachable once zoomedIn is
// true, which (via handleZoom, registered on the axis inside createChart, in the same call that
// sets chart state) can only happen once chart itself is already set - making the guard
// unreachable through an actual render, the same reasoning as deviceAnomaliesEventChart.tsx's own
// applySelectedDateHighlight guard.
export function resetZoom(chart: am4charts.XYChart | undefined, setZoomedIn: (zoomedIn: boolean) => void) {
  setZoomedIn(false);
  if (chart) {
    const xAxis = chart.xAxes.values[0] as am4charts.DateAxis;
    xAxis.zoomToDates(new Date(xAxis.min), new Date(xAxis.max));
  }
}

// Re-applies each event marker's tick length/label offset against the CURRENT plot height.
// onDataUpdated (in the component below) also computes this, but it runs synchronously as part of
// creating the chart or reacting to a prop change - before amCharts has actually laid out the plot
// area (its own invalidation/layout cycle is asynchronous), so plotContainer.pixelHeight it reads
// there can still be 0 (or a stale, hidden-mount value) even on the call meant to reveal the
// chart. Registered against the chart's own 'validated' event (see createChart below) rather than
// 'ready' - confirmed live (via temporary useAlignCharts logging while debugging the width sync
// above) that 'ready' only ever fires once per chart's lifetime, despite useAlignCharts' own
// comment assuming it persistently refires; a 'ready' listener attached at chart creation, as this
// one originally was, only ever catches that single firing, typically while still hidden (height
// 0), and never runs again. 'validated' is what AmChart's own onDataValidated already relies on
// (see the chartReady state toggle below) to reliably fire after every real validation pass,
// including the one after onDataUpdated's own deepInvalidate() call once the chart actually
// becomes visible.
// Exported (rather than left inline in the component) so its own "range has no label" guard can
// be exercised directly by a unit test - every real event marker this component ever creates
// (addEventOverlaysToLineChart's own createEventLine) always sets one, making that branch
// unreachable through an actual render, same reasoning as resetZoom above.
export function applyEventTickHeights(targetChart: am4charts.XYChart) {
  const height = targetChart.plotContainer.pixelHeight;
  if (!height) {
    return;
  }
  const xAxis = targetChart.xAxes.values[0] as am4charts.DateAxis;
  xAxis.axisRanges.each((range) => {
    range.tick.length = height;
    if (range.label) {
      range.label.dy = -(height + LABEL_DY_BEYOND_TICK);
    }
  });
}

interface ICommonDeviceEventsTimelineChartProps {
  /** The examined day's real UTC bounds. */
  dateRange: { from: number, to: number },
  eventFilters: IDeviceEventFilterState,
  dispatchEventFilters: React.Dispatch<EditEventsAction<AnomalyChartName>>,
  visible: boolean,
  onMount: (chart: am4charts.XYChart) => am4charts.XYChart,
  title: string,
  cypressId: string,
  /** Passed straight through to EditEvents - the caller's own locked, non-disableable event
   * marker (e.g. deviceRebooted for RebootsSection, deviceDropped for DropsSection). See
   * useAnomalyExaminationEventFilters's own lockedEvents for the matching state-side guard. */
  lockedEvents: readonly DeviceEventName[],
  /** Optional x-axis title (e.g. an italicised "Device Local Time (...)" - see
   * DeviceChargeLevel's own xAxisTitle convention). Left unset leaves the axis unlabelled,
   * matching this component's previous behaviour. */
  xAxisTitle?: string
}
export type DeviceEventsTimelineChartProps = ICommonDeviceEventsTimelineChartProps & (
  { displayTimeZone?: undefined, events: DeviceEvent[] } |
  { displayTimeZone: string, events: TzDeviceEvent[] }
);

function setLoadingNoop() {
  // No separate "loading" state to flip while an edit is applied - unlike DeviceChargeLevel, this
  // chart's data is already in memory (see eventTimelineSection.tsx), so applying a filter change
  // never triggers a new fetch.
}

export function DeviceEventsTimelineChart(props: DeviceEventsTimelineChartProps) {
  const { dateRange, displayTimeZone, events, eventFilters, dispatchEventFilters, visible, onMount, title, cypressId, lockedEvents, xAxisTitle } = props;
  const { platformType } = useContext(DeviceInfoContext);
  const featureToggles = useFeatureTogglesContext();
  const { t } = useTranslation(['anomalyDetectionBeta', 'editEvents', 'translation']);
  const [zoomedIn, setZoomedIn] = useState(false);
  const [chart, setChart] = useState<am4charts.XYChart | undefined>(undefined);
  const [dataValidated, setDataValidated] = useState(false);
  // AmChart's own onDataValidated (below) fires as soon as amCharts validates whatever data it
  // currently has - including the very first pass against buildExtentData's placeholder points,
  // synchronously at mount, well before eventTimelineSection.tsx's own fetch (and hence `visible`)
  // has resolved. Gating on visible too means chart-ready (and cypress's own TIMELINE_CHART_READY
  // lookups) only ever appears once the section's real data has actually loaded and the chart has
  // become genuinely visible - not merely once amCharts has validated a still-hidden placeholder.
  const chartReady = dataValidated && visible;

  // Reuses the same session-only reducer LowPowerSection's battery chart dispatches into, but
  // with its own dedicated anomalyChartNames.deviceEventsTimeline identity (rather than borrowing
  // DeviceChargeLevel's chartNames.deviceChargeLevel) - useAnomalyExaminationEventFilters's own
  // reducer never inspects name regardless, since it only ever manages a single chart's state, but
  // a distinct, non-persisted AnomalyChartName keeps this chart from being confused with the real,
  // persisted performance-tab ChartName of a similarly-named chart.
  const { handleUpdate, handleApply, handleCancel } = useEditEventsDispatch(
    dispatchEventFilters, anomalyChartNames.deviceEventsTimeline, setLoadingNoop
  );

  // Mirrors DeviceChargeLevel's own zoomedIn check - a full, un-zoomed axis already reports a
  // start/end fractionally short of exactly 0/1, so comparing against a fixed threshold rather
  // than exact 0/1 avoids reporting "zoomed" immediately on mount.
  function handleZoom(xAxis: am4charts.DateAxis) {
    setZoomedIn(xAxis.start > 0.001 || xAxis.end < 0.999);
  }

  function setAxisBounds(xAxis: am4charts.DateAxis) {
    xAxis.min = dateRange.from;
    xAxis.max = dateRange.to;
  }

  function createChart(id: string) {
    am4core.useTheme(coreTheme);

    const newChart: am4charts.XYChart = am4core.create(id, am4charts.XYChart);
    newChart.paddingTop = 42;
    newChart.height = am4core.percent(100);

    const xAxis = new am4charts.DateAxis();
    newChart.xAxes.push(xAxis);
    xAxis.dataFields.date = 'local';
    xAxis.groupData = false;
    xAxis.renderer.minGridDistance = 50;
    xAxis.renderer.grid.template.strokeWidth = 0;
    // Matches the grid/baseGrid stroke color coreTheme applies to every other AxisRenderer in the
    // app (see amChart.tsx) - the axis line itself isn't touched by that shared theme, and
    // defaults to invisible here, so it's set explicitly to give the timeline a visible baseline.
    xAxis.renderer.line.strokeOpacity = 1;
    xAxis.renderer.line.stroke = am4core.color('#C2D1E0');
    xAxis.dateFormatter.utc = true;
    formatDateAxisLabelsInTimeZone(xAxis, displayTimeZone);
    // Matches DeviceChargeLevel's own DateAxis formatting exactly: without this, amCharts' default
    // behaviour shows the full date (e.g. "Sep 06") at the axis's very first label and again at
    // any actual day-boundary crossing, then just the time otherwise - collapsing the day-boundary
    // format down to just the day-of-month number, and disabling markUnitChange's own "show extra
    // context at unit changes" behaviour, keeps every label a plain time throughout, since this
    // chart already conveys the date via its own title ("Events Timeline for {{from}} to {{to}}").
    xAxis.dateFormats.setKey('day', 'dt');
    xAxis.markUnitChange = false;
    if (xAxisTitle) {
      // See deviceEventsTimelineChart.css's own "taller than DeviceChargeLevel's 14rem convention"
      // comment on .chart-container - that height was already set without a title in mind, so
      // (mirroring DeviceChargeLevel's own xAxisTitle comment/charts.css compensation) giving the
      // title a non-empty value costs this chart part of its existing, fixed-height container
      // rather than growing to fit it.
      xAxis.title.text = xAxisTitle;
    }
    // This chart's only meaningful hover content is each event marker's own dedicated tooltip
    // (set up by addEventOverlaysToLineChart below), not a generic axis-position tooltip - as with
    // DeviceChargeLevel (which sets this for the same reason), leaving the default axis cursor
    // tooltip enabled makes amCharts do extra work resolving/rendering it on every mousemove,
    // competing with the per-marker tooltips and locking up click/hover handling entirely.
    xAxis.cursorTooltipEnabled = false;
    // Matches the backing series' own EXTENT_STEP_MINUTES spacing exactly - without this, amCharts
    // has to auto-detect the base interval (and, combined with configureDragToZoom's cursor and
    // scrollbar, search/construct grid intervals) from the data itself on every validation, which
    // is expensive on a DateAxis and was pegging the CPU on mount. DeviceChargeLevel and
    // wifiSignalChart both set this explicitly for the same reason.
    xAxis.baseInterval = { timeUnit: 'minute', count: EXTENT_STEP_MINUTES };
    xAxis.gridIntervals.setAll([
      { timeUnit: 'minute', count: 5 },
      { timeUnit: 'minute', count: 10 },
      { timeUnit: 'minute', count: 30 },
      { timeUnit: 'hour', count: 1 },
      { timeUnit: 'hour', count: 3 },
      { timeUnit: 'hour', count: 6 },
      { timeUnit: 'hour', count: 12 }
    ]);
    setAxisBounds(xAxis);
    // Without this, ValueAxis.dataChangeUpdate resets start/end back to 0/1 (and re-dispatches
    // startendchanged accordingly) every time this chart's data is invalidated - including the
    // become-visible transition's own AmChart.componentDidUpdate -> invalidateData() call (see
    // hideChildren in eventTimelineSection.tsx) - silently discarding a zoom applied before that point.
    // keepSelection instead preserves the current start/end fraction across those refreshes,
    // matching what a user would expect from an interactive zoomed chart whose data quietly
    // reloads underneath it.
    xAxis.keepSelection = true;
    xAxis.events.on('startendchanged', () => handleZoom(xAxis));

    // A bare timeline: the y-axis exists only so useAlignCharts (via onMount, below) has a real
    // axis to measure and keep in step with the top chart's own y-axis width. Deliberately left
    // as a genuinely real, fully-functional axis (real grid/labels/ticks/line, same as
    // DeviceChargeLevel's own y-axis) rather than disabling those renderer templates - disabling
    // them takes amCharts down a code path that never creates or measures real label/grid
    // elements at all, which (combined with useAlignCharts forcing an explicit pixel width via
    // axis.width) hung amCharts and the whole tab. Hiding the whole thing via axis.opacity
    // instead is purely a paint-time property: AxisRenderer's own constructor does
    // `this.gridContainer.bind("opacity", axis)`, so this cascades to the grid/ticks/labels (which
    // live in gridContainer, itself reparented into plotContainer) and the axis line, without ever
    // skipping the real measurement codepath DeviceChargeLevel's working axis already relies on.
    const yAxis = new am4charts.ValueAxis();
    newChart.yAxes.push(yAxis);
    // Deliberately no explicit min/max/strictMinMax here - see buildExtentData's own comment for
    // why: the backing series' genuinely varying 0/1 data lets normal auto-scaling settle on a
    // stable range on its own.
    yAxis.tooltip.disabled = true;
    yAxis.cursorTooltipEnabled = false;
    yAxis.contentAlign = 'right';
    yAxis.opacity = 0;

    const series = newChart.series.push(new am4charts.LineSeries());
    series.dataFields.dateX = 'local';
    series.dataFields.valueY = 'value';
    series.strokeWidth = 0;
    series.fillOpacity = 0;
    series.tooltip.disabled = true;

    // Also sets up the scrollbar used to pan once zoomed in - its own startendchanged listener
    // (registered here) manages the cursor's enabled/disabled state and behavior based on zoom
    // level, so lineY/lineX are deliberately left as it sets them rather than overridden further.
    configureDragToZoom(newChart, xAxis);
    // No meaningful Y position to report either, and an unset default lets the cursor search an
    // unbounded distance for a "nearest" tooltip target on every mousemove - matches
    // DeviceChargeLevel's own maxTooltipDistance = 0.
    newChart.cursor.maxTooltipDistance = 0;
    // See applyEventTickHeights's own comment for why this can't just be computed once, eagerly,
    // in onDataUpdated, and why 'validated' rather than 'ready'.
    newChart.events.on('validated', () => applyEventTickHeights(newChart));

    setChart(newChart);
    return newChart;
  }

  function onDataUpdated(updatedChart: am4charts.XYChart) {
    setAxisBounds(updatedChart.xAxes.values[0] as am4charts.DateAxis);
    // pixelHeight reads 0 while the chart is still mounted hidden (see hideChildren in
    // eventTimelineSection.tsx) - falls back to FALLBACK_TICK_LENGTH until a real, visible layout pass
    // has actually measured it; onDataUpdated re-runs (see the visible check below) once it has.
    const tickLength = updatedChart.plotContainer.pixelHeight || FALLBACK_TICK_LENGTH;
    addEventOverlaysToLineChart(updatedChart, updatedChart.xAxes.values[0] as am4charts.DateAxis, events, t, {
      tickLength,
      labelDy: -(tickLength + LABEL_DY_BEYOND_TICK),
      displayTimeZone
    });
    // As with DeviceChargeLevel: this chart can be mounted hidden (while the section's data is
    // still loading) via hideChildren - amCharts can lay itself out against that hidden, zero-size
    // container and not self-correct once it becomes visible again, so force a full redraw the
    // first time visible flips true.
    if (visible) {
      updatedChart.deepInvalidate();
    }
    return updatedChart;
  }

  return (
    // data-id mirrors stackedColumnsChart.tsx's own cypressId + '--chart-ready' convention (see
    // deviceAnomaliesEventChart.spec.tsx's identical use via getByTestId, which reads data-id -
    // see .eslintrc.yaml) so tests can await readiness without raw container/node access. The
    // chart-ready class itself is kept alongside it for Cypress (core_device_anomaly_examination_
    // reboots.cy.js's TIMELINE_CHART_READY), mirroring DeviceChargeLevel's own class-only convention.
    <div
      className={`device-events-timeline-chart${chartReady ? ' chart-ready' : ''}`}
      data-id={`${cypressId}${chartReady ? '--chart-ready' : ''}`}
    >
      <div className="chart-header">
        <div className="chart-header-left">
          <NoSelectionOverlay noSelectionText={t('NO_DATA_AVAILABLE', { ns: 'translation' })} show={events.length === 0} />
          <div className="chart-title">{title}</div>
          <ResetZoomButton zoomedIn={zoomedIn} resetZoom={() => resetZoom(chart, setZoomedIn)} />
        </div>
        <div className="chart-edit-events">
          <EventsShown eventFilters={eventFilters.appliedEventFilters} />
          <EditEvents
            subheader={`‘${title}’`}
            eventGroupNames={deviceEventFilterGroupNames}
            eventFilterGroups={getDeviceEventFilterGroups(platformType, featureToggles)}
            eventFilterState={eventFilters.currentEventFilters}
            lockedEvents={lockedEvents}
            handleUpdate={handleUpdate}
            handleApply={handleApply}
            handleCancel={handleCancel}
          />
        </div>
      </div>
      <div className="chart-and-legend-container">
        <div className="chart-container">
          <AmChart
            tag="device-events-timeline-chart"
            chartProvider={createChart}
            onMount={(mountedChart) => { onMount(mountedChart); return onDataUpdated(mountedChart); }}
            onUpdate={onDataUpdated}
            dataProvider={() => buildExtentData(dateRange)}
            link={{ dateRange, displayTimeZone, events, visible }}
            onDataValidated={() => setDataValidated(true)}
            cypressId={cypressId}
          />
        </div>
      </div>
    </div>
  );
}
